<script setup>
import { useAlert } from 'dashboard/composables';
import AddCanned from './AddCanned.vue';
import EditCanned from './EditCanned.vue';
import CannedResponseCard from './CannedResponseCard.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import { computed, onMounted, ref, defineOptions } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  useStoreGetters,
  useStore,
  useMapGetter,
} from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';

defineOptions({
  name: 'CannedResponseSettings',
});

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const showAddPopup = ref(false);
const loading = ref({});
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const activeResponse = ref({});
const cannedResponseAPI = ref({ message: '' });

const searchQuery = ref('');
const sortOrder = ref('asc');
const selectedTeamId = ref(null);

const allRecords = computed(() => getters.getCannedResponses.value);
const uiFlags = computed(() => getters.getUIFlags.value);
const teamsList = useMapGetter('teams/getTeams');

const teamOptions = computed(() => {
  const options = [{ value: null, label: t('CANNED_MGMT.FILTER.ALL_TEAMS') }];
  teamsList.value.forEach(team => {
    options.push({ value: team.id, label: team.name });
  });
  return options;
});

const filteredRecords = computed(() => {
  let result = [...allRecords.value];

  // Filter by search query (match short_code or content)
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.trim().toLowerCase();
    result = result.filter(
      record =>
        record.short_code.toLowerCase().includes(query) ||
        record.content.toLowerCase().includes(query)
    );
  }

  // Filter by selected team
  if (selectedTeamId.value) {
    result = result.filter(
      record =>
        record.team_ids && record.team_ids.includes(selectedTeamId.value)
    );
  }

  // Sort by short_code
  result.sort((a, b) => {
    if (sortOrder.value === 'asc') {
      return a.short_code.localeCompare(b.short_code);
    }
    return b.short_code.localeCompare(a.short_code);
  });

  return result;
});

const sortOptions = [
  { value: 'asc', label: t('CANNED_MGMT.SORT.ASC') },
  { value: 'desc', label: t('CANNED_MGMT.SORT.DESC') },
];

const deleteConfirmText = computed(
  () =>
    `${t('CANNED_MGMT.DELETE.CONFIRM.YES')} ${activeResponse.value.short_code}`
);

const deleteRejectText = computed(
  () =>
    `${t('CANNED_MGMT.DELETE.CONFIRM.NO')} ${activeResponse.value.short_code}`
);

const deleteMessage = computed(() => {
  return ` ${activeResponse.value.short_code} ? `;
});

const fetchCannedResponses = async () => {
  try {
    await store.dispatch('getCannedResponse', { filter: 'all' });
  } catch {
    // Ignore Error
  }
};

onMounted(() => {
  fetchCannedResponses();
  store.dispatch('teams/get');
});

const showAlertMessage = message => {
  loading[activeResponse.value.id] = false;
  activeResponse.value = {};
  cannedResponseAPI.value.message = message;
  useAlert(message);
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = response => {
  showEditPopup.value = true;
  activeResponse.value = response;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  activeResponse.value = response;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteCannedResponse = async id => {
  try {
    await store.dispatch('deleteCannedResponse', id);
    showAlertMessage(t('CANNED_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('CANNED_MGMT.DELETE.API.ERROR_MESSAGE');
    showAlertMessage(errorMessage);
  }
};

const confirmDeletion = () => {
  loading[activeResponse.value.id] = true;
  closeDeletePopup();
  deleteCannedResponse(activeResponse.value.id);
};
</script>

<template>
  <div class="flex-1 overflow-auto">
    <BaseSettingsHeader
      :title="$t('CANNED_MGMT.HEADER')"
      :description="$t('CANNED_MGMT.DESCRIPTION')"
      :link-text="$t('CANNED_MGMT.LEARN_MORE')"
      feature-name="canned_responses"
    >
      <template #actions>
        <Button
          icon="i-lucide-circle-plus"
          :label="$t('CANNED_MGMT.HEADER_BTN_TXT')"
          @click="openAddPopup"
        />
      </template>
    </BaseSettingsHeader>

    <div class="mt-6 flex-1">
      <!-- Search, Sort, and Team Filter Bar -->
      <div class="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center">
        <div class="flex-1">
          <div class="relative">
            <div
              class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none"
            >
              <svg
                class="w-4 h-4 text-n-slate-10"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              v-model="searchQuery"
              type="text"
              :placeholder="$t('CANNED_MGMT.SEARCH.PLACEHOLDER')"
              class="block w-full reset-base text-sm h-10 pl-10 pr-3 py-2.5 mb-0 outline outline-1 border-none border-0 outline-offset-[-1px] rounded-lg bg-n-alpha-black2 outline-n-weak dark:outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 focus:outline-n-brand dark:focus:outline-n-brand placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 text-n-slate-12 transition-all duration-500 ease-in-out"
            />
          </div>
        </div>
        <div class="flex items-center gap-2 shrink-0">
          <SelectMenu
            v-model="selectedTeamId"
            :options="teamOptions"
            :label="
              teamOptions.find(o => o.value === selectedTeamId)?.label ||
              $t('CANNED_MGMT.FILTER.ALL_TEAMS')
            "
            sub-menu-position="bottom"
          />
          <SelectMenu
            v-model="sortOrder"
            :options="sortOptions"
            :label="
              sortOrder === 'asc'
                ? $t('CANNED_MGMT.SORT.ASC')
                : $t('CANNED_MGMT.SORT.DESC')
            "
            sub-menu-position="bottom"
          />
        </div>
      </div>

      <woot-loading-state
        v-if="uiFlags.fetchingList"
        :message="$t('CANNED_MGMT.LOADING')"
      />
      <p
        v-else-if="!filteredRecords.length && !searchQuery && !selectedTeamId"
        class="flex flex-col items-center justify-center h-full text-base text-slate-600 dark:text-slate-300 py-8"
      >
        {{ $t('CANNED_MGMT.LIST.404') }}
      </p>
      <p
        v-else-if="!filteredRecords.length"
        class="flex flex-col items-center justify-center h-full text-base text-slate-600 dark:text-slate-300 py-8"
      >
        {{ $t('CANNED_MGMT.SEARCH_404') }}
      </p>
      <div v-else class="flex flex-col gap-4">
        <CannedResponseCard
          v-for="cannedItem in filteredRecords"
          :key="cannedItem.short_code"
          :canned-item="cannedItem"
          @edit="openEditPopup"
          @delete="openDeletePopup"
        />
      </div>
    </div>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddCanned :on-close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditCanned
        v-if="showEditPopup"
        :id="activeResponse.id"
        :edshort-code="activeResponse.short_code"
        :edcontent="activeResponse.content"
        :ed-team-ids="activeResponse.team_ids"
        :on-close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('CANNED_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('CANNED_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />
  </div>
</template>
